import React, { useEffect, useMemo, useState } from "react";
import { Box, Divider, FormControlLabel, MenuItem, Stack, Switch, TextField, Typography } from "@mui/material";
import FormDrawer from "../shared/FormDrawer";
import ImageUploadField from "../shared/ImageUploadField";
import { BANNER_TYPES } from "../../../api/preferences/appContentApi";
import { BANNER_IMAGE_SPECS, formatSpec } from "../../../utils/bannerImageSpecs";

const ACCENTS = {
  [BANNER_TYPES.HOME]: "#2563eb",
  [BANNER_TYPES.POPUP]: "#7c3aed",
  [BANNER_TYPES.ANNOUNCEMENT]: "#ea580c",
};

const TYPE_OPTIONS = [
  { value: BANNER_TYPES.HOME, label: "Home Hero" },
  { value: BANNER_TYPES.POPUP, label: "Popup" },
  { value: BANNER_TYPES.ANNOUNCEMENT, label: "Announcement" },
];

const emptyForm = {
  type: TYPE_OPTIONS[0].value,
  title: "",
  description: "",
  linkUrl: "",
  displayOrder: "",
  scheduleStart: "",
  scheduleEnd: "",
  isActive: true,
  imageOnly: false,
  locationType: "all",
  placeName: "",
  latitude: "",
  longitude: "",
  radiusKm: "10",
};

// Create/Edit drawer for an App Popups banner (Popup / Announcement). `banner`
// is null for create, or the normalized row object for edit. Builds a
// multipart FormData payload and hands it, plus the selected Type, back to
// the parent's onSave — the parent (AppPopupsManager) owns the actual
// create/update API call since it knows which bannerType collection to hit.
// The Type field is locked once editing an existing banner: the backend has
// no endpoint to move a banner between bannerType collections.
const BannerFormDrawer = ({ open, banner, saving, onClose, onSave }) => {
  const [form, setForm] = useState(emptyForm);
  const [image, setImage] = useState(null);
  // Remove on an already-saved banner only clears the preview: the upload box
  // has to come back, and the saved image stays on the banner until a new file
  // actually replaces it (the API has no "delete the image" update).
  const [existingCleared, setExistingCleared] = useState(false);
  const [errors, setErrors] = useState({});

  const accentColor = ACCENTS[form.type] || "#7c3aed";
  // Each banner type renders on a differently shaped surface in the app, so
  // the upload field is locked to that type's exact size. The design note
  // flips with "Image already has text": that mode turns the app's own
  // overlay off, so the artwork must be the finished creative instead of a
  // plain background. Memoized — ImageUploadField resets its rejection
  // message whenever the spec identity changes.
  const imageSpec = useMemo(() => {
    const base = BANNER_IMAGE_SPECS[form.type];
    if (!base) return null;
    if (!form.imageOnly) return base;
    return {
      ...base,
      note: "Finished creative — the app shows this image alone, with no title, description or button over it. Keep important content away from the rounded corners.",
      // No app-drawn overlay in this mode, so the crop dialog must not shade
      // the bottom of the frame as if there were one.
      overlayBottomPct: 0,
    };
  }, [form.type, form.imageOnly]);

  useEffect(() => {
    if (open) {
      setForm(
        banner
          ? {
              type: banner.bannerType || TYPE_OPTIONS[0].value,
              title: banner.title || "",
              description: banner.description || "",
              linkUrl: banner.linkUrl || "",
              displayOrder: banner.displayOrder ?? "",
              scheduleStart: banner.scheduleStart ? banner.scheduleStart.slice(0, 10) : "",
              scheduleEnd: banner.scheduleEnd ? banner.scheduleEnd.slice(0, 10) : "",
              isActive: banner.isActive ?? true,
              imageOnly: banner.imageOnly ?? false,
              locationType: banner.locationType || "all",
              placeName: banner.placeName || "",
              latitude: banner.latitude ?? "",
              longitude: banner.longitude ?? "",
              radiusKm: banner.radiusKm ?? "10",
            }
          : emptyForm
      );
      setImage(null);
      setExistingCleared(false);
      setErrors({});
    }
  }, [open, banner]);

  const handleImageChange = (file) => {
    setImage(file);
    if (file) setExistingCleared(false);
    if (errors.image) setErrors((prev) => ({ ...prev, image: null }));
  };

  const handleImageRemove = () => {
    setImage(null);
    setExistingCleared(true);
  };

  const handleChange = (field) => (e) => {
    const value = field === "isActive" || field === "imageOnly" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
    // Switching type switches the required image size, so an already-picked
    // file is no longer valid — drop it and make the user upload the right one.
    if (field === "type") {
      setImage(null);
      setErrors((prev) => ({ ...prev, image: null }));
    }
  };

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = "Title is required";
    if (!image && (!banner || existingCleared)) e.image = "Banner image is required";
    if (form.scheduleStart && form.scheduleEnd && form.scheduleEnd < form.scheduleStart) {
      e.scheduleEnd = "Must be after Schedule Start";
    }
    if (form.locationType === "specific" &&
        (!form.placeName.trim() || form.latitude === "" || form.longitude === "" || Number(form.radiusKm) <= 0)) {
      e.location = "Place, coordinates and a positive radius are required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    const fd = new FormData();
    fd.append("title", form.title.trim());
    fd.append("description", form.description.trim());
    fd.append("linkUrl", form.linkUrl.trim());
    fd.append("displayOrder", form.displayOrder === "" ? "0" : String(Number(form.displayOrder)));
    fd.append("scheduleStart", form.scheduleStart || "");
    fd.append("scheduleEnd", form.scheduleEnd || "");
    fd.append("isActive", String(form.isActive));
    fd.append("imageOnly", String(form.imageOnly));
    fd.append("locationType", form.locationType);
    fd.append("placeName", form.placeName.trim());
    fd.append("latitude", String(form.latitude));
    fd.append("longitude", String(form.longitude));
    fd.append("radiusKm", String(form.radiusKm));
    if (image) fd.append("image", image);
    onSave(fd, form.type);
  };

  return (
    <FormDrawer
      open={open}
      onClose={onClose}
      title={banner ? "Edit Banner" : "Create Banner"}
      subtitle="APP CONTENT"
      saving={saving}
      onSave={handleSave}
      saveLabel={banner ? "Update" : "Create"}
      accentColor={accentColor}
    >
      <Stack spacing={2.5}>
        <ImageUploadField
          label="Banner Image"
          required={!banner || existingCleared}
          file={image}
          existingUrl={existingCleared ? null : banner?.image}
          onFileChange={handleImageChange}
          onRemove={handleImageRemove}
          error={errors.image}
          height={160}
          spec={imageSpec}
        />

        <FormControlLabel
          control={<Switch checked={form.imageOnly} onChange={handleChange("imageOnly")} color="primary" />}
          label={
            <Box>
              <Typography variant="body2" fontWeight={600}>Image already has text</Typography>
              <Typography variant="caption" color="text.secondary">
                {form.type === BANNER_TYPES.HOME
                  ? "On: the app hides its dark gradient, title and Bike Service button, and shows your creative as-is. Turn this on for ready-made posters."
                  : "On: the app hides the title and description under the image and shows your creative as-is."}
              </Typography>
            </Box>
          }
        />

        <Divider />

        <TextField
          select
          fullWidth
          label="Type"
          value={form.type}
          onChange={handleChange("type")}
          disabled={Boolean(banner)}
          helperText={
            banner
              ? "Type cannot be changed after creation"
              : `Choose where this appears in the app — image must be ${formatSpec(imageSpec)}`
          }
          size="small"
          InputLabelProps={{ shrink: true }}
        >
          {TYPE_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          label="Title"
          value={form.title}
          onChange={handleChange("title")}
          error={!!errors.title}
          helperText={errors.title}
          placeholder="e.g. Monsoon Service Offer"
          size="small"
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Description"
          value={form.description}
          onChange={handleChange("description")}
          helperText="Optional supporting text shown on the banner"
          size="small"
        />

        <TextField
          fullWidth
          label="Link URL"
          value={form.linkUrl}
          onChange={handleChange("linkUrl")}
          helperText="Where tapping this banner navigates to"
          placeholder="e.g. /offers/monsoon-service"
          size="small"
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          fullWidth
          type="number"
          label="Display Order"
          value={form.displayOrder}
          onChange={handleChange("displayOrder")}
          helperText="Lower numbers appear first"
          size="small"
          InputLabelProps={{ shrink: true }}
        />

        <Divider />

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            fullWidth
            type="date"
            label="Schedule Start Date"
            value={form.scheduleStart}
            onChange={handleChange("scheduleStart")}
            size="small"
            InputLabelProps={{ shrink: true }}
            helperText="Optional"
          />
          <TextField
            fullWidth
            type="date"
            label="Schedule End Date"
            value={form.scheduleEnd}
            onChange={handleChange("scheduleEnd")}
            error={!!errors.scheduleEnd}
            helperText={errors.scheduleEnd || "Optional"}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        </Stack>

        <Divider />

        <TextField
          select
          fullWidth
          label="Banner Location"
          value={form.locationType}
          onChange={handleChange("locationType")}
          size="small"
        >
          <MenuItem value="all">All locations</MenuItem>
          <MenuItem value="specific">Specific location</MenuItem>
        </TextField>
        {form.locationType === "specific" && (
          <>
            <TextField fullWidth label="Place Name" value={form.placeName} onChange={handleChange("placeName")} error={!!errors.location} helperText={errors.location} size="small" />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField fullWidth type="number" label="Latitude" value={form.latitude} onChange={handleChange("latitude")} size="small" />
              <TextField fullWidth type="number" label="Longitude" value={form.longitude} onChange={handleChange("longitude")} size="small" />
              <TextField fullWidth type="number" label="Radius (km)" value={form.radiusKm} onChange={handleChange("radiusKm")} size="small" />
            </Stack>
          </>
        )}

        <Divider />

        <FormControlLabel
          control={<Switch checked={form.isActive} onChange={handleChange("isActive")} color="success" />}
          label={
            <Box>
              <Typography variant="body2" fontWeight={600}>Active Status</Typography>
              <Typography variant="caption" color="text.secondary">Inactive banners are hidden from the customer app</Typography>
            </Box>
          }
        />
      </Stack>
    </FormDrawer>
  );
};

export default BannerFormDrawer;
